import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { ChevronRight, House, Image, Menu, Search, X } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useState, type ReactNode } from 'react';

import { stopImpersonation, useSession } from '@/shared/auth';
import { t } from '@/shared/i18n';
import { springs } from '@/shared/lib/motion';
import { useSidebar } from '@/shared/store/sidebar';
import { Button } from '@/shared/ui/Button';
import { Crumbs } from '@/shared/ui/Crumbs';
import { toast } from '@/shared/ui/Toast';
import { TooltipProvider } from '@/shared/ui/Tooltip';

import { ConnectionBar } from '@/features/presence/ConnectionBar';

import { useLiveSite } from '../site/useLiveSite';
import { useSiteScope, type SiteScope } from '../site/useSiteScope';

import { Backdrop } from './Backdrop';
import { CommandPalette } from './CommandPalette';
import { targetPath } from './navigation';
import { RouterAnchor } from './RouterAnchor';
import { Sidebar } from './Sidebar';
import { useTrailItems, type TrailItem } from './trail';
import styles from './AppShell.module.scss';

// Подпись клавиши в кнопке поиска: ⌘ на Mac, Ctrl на остальных.
const modKey =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl+';

/**
 * Оболочка админки — по образцу flow-do.
 *
 * На широком экране колонка слева всегда, её можно свернуть до иконок. На
 * телефоне колонки нет: внизу панель под одну руку, а полное меню выезжает
 * по кнопке «Меню» (docs/research/07-navigation-and-home.md).
 *
 * Над страницей — всё, что отвечает на «где я»: полоса входа от чужого
 * имени, полоса Development и хлебные крошки.
 */
export function AppShell() {
  const scope = useSiteScope();
  useLiveSite(scope?.siteId, scope?.env);
  const collapsed = useSidebar((state) => state.collapsed);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const trail = useTrailItems();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Панель помнит адрес, на котором её открыли, и открыта, только пока он
  // тот же: любой переход закрывает её сам, без эффектов и setState.
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const drawerOpen = openedAt === pathname;

  // Esc закрывает панель; пока она открыта, страница под ней не крутится.
  useEffect(() => {
    if (!drawerOpen) {
      return;
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenedAt(null);
      }
    };

    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  useDocumentTitle(scope);
  useDevOnlyRedirect(scope, pathname);

  const crumbs = crumbsFor(pathname, scope, trail);
  const openPalette = () => {
    setPaletteOpen(true);
  };

  return (
    <TooltipProvider delayDuration={400}>
      <div className={styles['shell']}>
        <Backdrop />

        {/* Ширина колонки анимируется пружиной; содержимое справа
            подтягивается вместе с ней — это одна layout-операция. */}
        <motion.aside
          className={styles['rail']}
          initial={false}
          animate={{ width: collapsed ? 64 : 248 }}
          transition={springs.rubber}
        >
          <Sidebar scope={scope} />
        </motion.aside>

        {/* Выдвижная панель на телефоне — без анимации: пружина на transform
            поверх размытого фона заметно грузит телефон (вывод flow-do). */}
        {drawerOpen && (
          <>
            <button
              type="button"
              className={styles['scrim']}
              aria-label={t.nav.closeMenu}
              onClick={() => {
                setOpenedAt(null);
              }}
            />
            <aside className={styles['drawer']}>
              <Sidebar
                scope={scope}
                collapsible={false}
                onNavigate={() => {
                  setOpenedAt(null);
                }}
              />
              <button
                type="button"
                className={styles['drawer__close']}
                aria-label={t.nav.closeMenu}
                onClick={() => {
                  setOpenedAt(null);
                }}
              >
                <X />
              </button>
            </aside>
          </>
        )}

        <div className={styles['main']}>
          <div className={styles['top']}>
            <ImpersonationBar />

            {/* Полоса — на всю ширину и несъёмная: цвет, текст и положение
                вместе, а не один цвет, который не различит дальтоник. */}
            {scope?.env === 'dev' && (
              <div className={styles['env-stripe']} role="status">
                {t.env.stripe}
              </div>
            )}

            <header className={styles['topbar']}>
              <Crumbs current={pathname} className={styles['crumbs']}>
                {crumbs.map((item, index) => (
                  <span key={`${item.label}-${String(index)}`} className={styles['crumb']}>
                    {index > 0 && <ChevronRight className={styles['crumb__sep']} aria-hidden />}
                    {item.href !== undefined && index < crumbs.length - 1 ? (
                      <RouterAnchor href={item.href} className={styles['crumb__link']}>
                        {item.label}
                      </RouterAnchor>
                    ) : (
                      <span className={styles['crumb__current']} aria-current="page">
                        {item.label}
                      </span>
                    )}
                  </span>
                ))}
              </Crumbs>

              <span className={styles['topbar__spacer']} />

              {/* Поиск — кнопка, а не поле: он открывает палитру, а не ищет на
                  месте. Поле обещало бы, что можно печатать прямо здесь. */}
              <button type="button" className={styles['search']} onClick={openPalette}>
                <Search />
                <span className={styles['search__text']}>{t.nav.searchHint}</span>
                <kbd className={styles['search__kbd']}>{modKey}K</kbd>
              </button>
            </header>
          </div>

          <ConnectionBar />

          <main className={styles['content']}>
            <Outlet />
          </main>
        </div>

        <MobileNav
          scope={scope}
          pathname={pathname}
          onSearch={openPalette}
          onMenu={() => {
            setOpenedAt(pathname);
          }}
        />

        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      </div>
    </TooltipProvider>
  );
}

/**
 * Полоса «вы работаете от имени…». Заметная и несъёмная: человек, забывший,
 * что смотрит на чужие данные, рано или поздно решит, что это его.
 */
function ImpersonationBar() {
  const session = useSession();

  if (session.status !== 'authenticated' || !session.impersonated) {
    return null;
  }

  return (
    <div className={styles['impersonation']} role="status">
      <span>
        {t.impersonation.text} <b>{session.user.email}</b>
      </span>
      <Button
        size="sm"
        variant="outline"
        tone="neutral"
        onClick={() => {
          stopImpersonation().catch(() => {
            toast.error(t.impersonation.backFailed);
          });
        }}
      >
        {t.impersonation.back}
      </Button>
    </div>
  );
}

/**
 * Нижняя панель на телефоне — под одну руку (NN/g: видимой навигацией
 * пользуются в 86% случаев, спрятанной в меню — в 57%).
 *
 * Пунктов столько, сколько разделов реально есть: «Входящие» появятся
 * вместе с поддержкой, а не пустым обещанием сейчас. Коллекции — на главной
 * сайта и в «Меню».
 */
function MobileNav({
  scope,
  pathname,
  onSearch,
  onMenu,
}: {
  scope: SiteScope | null;
  pathname: string;
  onSearch: () => void;
  onMenu: () => void;
}) {
  const base = scope === null ? null : `/sites/${scope.siteId}/${scope.env}`;
  const links: { href: string; icon: ReactNode; label: string }[] =
    base === null
      ? [{ href: '/sites', icon: <House />, label: t.nav.sites }]
      : [
          { href: base, icon: <House />, label: t.nav.home },
          { href: `${base}/media`, icon: <Image />, label: t.nav.media },
        ];

  return (
    <nav className={styles['bottom']} aria-label={t.nav.sections}>
      {links.map((link) => (
        <RouterAnchor
          key={link.href}
          href={link.href}
          className={styles['bottom__item']}
          aria-current={pathname === link.href ? 'page' : undefined}
        >
          {link.icon}
          <span>{link.label}</span>
        </RouterAnchor>
      ))}
      <button type="button" className={styles['bottom__item']} onClick={onSearch}>
        <Search />
        <span>{t.nav.search}</span>
      </button>
      <button type="button" className={styles['bottom__item']} onClick={onMenu}>
        <Menu />
        <span>{t.nav.more}</span>
      </button>
    </nav>
  );
}

/**
 * Хлебные крошки: сайт › окружение › раздел › то, что знает экран.
 *
 * Сайт, окружение и раздел выводятся из адреса и схемы. Хвост (например,
 * коллекция и название записи) сообщает сам экран через useTrail.
 */
function crumbsFor(pathname: string, scope: SiteScope | null, trail: TrailItem[]): TrailItem[] {
  if (scope === null) {
    if (pathname.startsWith('/tenants')) {
      return [{ label: t.nav.tenants }];
    }

    if (pathname.startsWith('/activity')) {
      return [{ label: t.nav.activity }];
    }

    return [{ label: t.nav.sites, href: '/sites' }, ...trail];
  }

  const base = `/sites/${scope.siteId}/${scope.env}`;
  const items: TrailItem[] = [
    { label: t.nav.sites, href: '/sites' },
    { label: scope.site?.name ?? '…', href: base },
    { label: scope.env === 'dev' ? t.env.development : t.env.production, href: base },
  ];

  const rest = pathname.slice(base.length);
  const model = /^\/(?:models|forms)\/([^/]+)/.exec(rest);
  const section = (path: string, label: string) => {
    items.push({ label, href: `${base}${path}` });
  };

  if (rest.startsWith('/models')) {
    section('/models', t.nav.models);

    if (model !== null && model[1] !== 'new') {
      const type = scope.types.find((item) => item.id === model[1]);
      items.push({ label: type?.name ?? '…' });
    }
  } else if (rest.startsWith('/forms')) {
    section('/forms', t.nav.forms);

    if (model !== null && model[1] !== 'new') {
      const type = scope.types.find((item) => item.id === model[1]);
      items.push({ label: type?.name ?? '…' });
    }
  } else if (rest.startsWith('/shop')) {
    section('/shop', t.nav.shop);
  } else if (rest.startsWith('/automations')) {
    section('/automations', t.nav.automations);
  } else if (rest.startsWith('/users')) {
    section('/users', t.nav.users);
  } else if (rest.startsWith('/components')) {
    section('/components', t.nav.components);
  } else if (rest.startsWith('/pages')) {
    section('/pages', t.nav.pages);
  } else if (rest.startsWith('/media')) {
    section('/media', t.nav.media);
  } else if (rest.startsWith('/constants')) {
    section('/constants', t.nav.constants);
  } else if (rest.startsWith('/navigation')) {
    section('/navigation', t.nav.navigation);
  } else if (rest.startsWith('/transfer')) {
    items.push({ label: t.nav.transfer });
  } else if (rest.startsWith('/settings')) {
    items.push({ label: t.nav.settings, href: `${base}/settings` });
  } else if (rest.startsWith('/team')) {
    items.push({ label: t.nav.team, href: `${base}/team` });
  } else if (rest.startsWith('/activity')) {
    items.push({ label: t.nav.activity, href: `${base}/activity` });
  }

  return [...items, ...trail];
}

/**
 * Человека с сужением «только Development» адрес Production уводит на тот же
 * раздел Development — закладка или чужая ссылка не должны показывать ему
 * пустой прод с отказами. Сервер прод ему и так не отдаёт.
 */
function useDevOnlyRedirect(scope: SiteScope | null, pathname: string) {
  const navigate = useNavigate();
  const target =
    scope?.devOnly === true && scope.env === 'prod'
      ? targetPath(pathname, scope.siteId, 'prod', 'dev')
      : undefined;

  useEffect(() => {
    if (target !== undefined) {
      void navigate({ href: target, replace: true });
    }
  }, [target, navigate]);
}

/**
 * Заголовок вкладки: сайт и метка [DEV] — среди десятка вкладок дев и прод
 * должны различаться и там, где полосы не видно.
 */
function useDocumentTitle(scope: SiteScope | null) {
  const name = scope?.site?.name;
  const dev = scope?.env === 'dev';

  useEffect(() => {
    if (name === undefined) {
      document.title = t.app.name;

      return;
    }

    const title = `${name} · ${t.app.name}`;
    document.title = dev ? `${t.env.tabPrefix} ${title}` : title;
  }, [name, dev]);
}
